import { type FC } from 'react';
import { connect } from 'react-redux';
import { useFragment, graphql } from 'react-relay';
import { GridRow, GridCol } from 'dibs-elements/exports/Grid.tsx';
import LinkColumnItem from './LinkColumn/LinkColumnItem.tsx';
import { legalLinks, mobileLegalLinks, copyrightLink } from './links.ts';
import RecaptchaTerms from 'dibs-recaptcha/exports/RecaptchaTerms.ts';
import { type FooterLink } from './FooterLinkTypes.ts';
import LogoMonogram from 'dibs-icons/exports/legacy/LogoMonogram.ts';
import { GlobalSitesLinks } from './GlobalSitesLinks/GlobalSitesLinks.tsx';
import { DisplaySettingsCta } from '../DisplaySettings/DisplaySettingsCta.tsx';
import { type FooterBottomRow_viewer$key } from './__generated__/FooterBottomRow_viewer.graphql.ts';

import dibsCss from 'dibs-css';
import classnames from 'classnames';
import style from './FooterBottomRow.scss';

type ReduxStoreType = {
    header: {
        isMobile: boolean;
        showGlobalSitesLinks: boolean;
    };
};

type Props = {
    filterLinks: (links: FooterLink[]) => FooterLink[];
    isMobile: boolean;
    showGlobalSitesLinks: boolean;
    viewer: FooterBottomRow_viewer$key;
};

const FooterBottomRow: FC<Props> = ({
    filterLinks,
    isMobile,
    showGlobalSitesLinks,
    viewer: viewerRef,
}) => {
    const viewer = useFragment(
        graphql`
            fragment FooterBottomRow_viewer on Viewer {
                user(userId: $userId) @include(if: $hasUserId) {
                    ...DisplaySettingsCta_user
                }
                ...DisplaySettingsCta_viewer
            }
        `,
        viewerRef
    );
    const linksToMap: FooterLink[] = isMobile ? mobileLegalLinks : legalLinks;

    return (
        <div
            className={classnames(
                style.footerBottomRow,
                dibsCss.ptMedium,
                dibsCss.sassyFontBodySizeXSmall
            )}
        >
            <GridRow align="top">
                <GridCol desktop={6}>
                    <ul className={classnames(dibsCss.flex, dibsCss.flexWrap)}>
                        {filterLinks(linksToMap).map(link => (
                            <LinkColumnItem key={link.dataTn} {...link} isLegalLink />
                        ))}
                    </ul>
                    {showGlobalSitesLinks && <GlobalSitesLinks />}
                    <RecaptchaTerms
                        wrapperClass={classnames(
                            style.recaptchaTermsWrapper,
                            dibsCss.sassyFontBodySizeXSmall,
                            dibsCss.mtSmall,
                            dibsCss.plXsmall
                        )}
                        linkType="contrastLinkAlternate"
                        linkClass={style.recaptchaLink}
                    />
                    {isMobile && (
                        <>
                            <ul className={style.copyrightLink}>
                                <LinkColumnItem
                                    key={copyrightLink.dataTn}
                                    {...copyrightLink}
                                    isLegalLink
                                />
                            </ul>
                            <DisplaySettingsCta
                                viewer={viewer}
                                user={viewer.user}
                                placement="mobileFooter"
                            />
                        </>
                    )}
                </GridCol>
                {!isMobile && (
                    <GridCol desktop={6}>
                        <div>
                            <DisplaySettingsCta viewer={viewer} user={viewer.user || null} />
                        </div>
                    </GridCol>
                )}
            </GridRow>
            <GridRow align="top">
                <GridCol desktop={12}>
                    <div
                        className={classnames(
                            style.monogramIconWrapper,
                            dibsCss.flex,
                            dibsCss.justifyCenter,
                            dibsCss.mtLarge
                        )}
                    >
                        <div
                            className={classnames(
                                dibsCss.wFull,
                                dibsCss.hXxlarge,
                                dibsCss.textCenter
                            )}
                        >
                            <LogoMonogram />
                        </div>
                    </div>
                </GridCol>
            </GridRow>
        </div>
    );
};

const mapStateToProps = ({
    header,
}: ReduxStoreType): {
    isMobile: boolean;
    showGlobalSitesLinks: boolean;
} => {
    const { isMobile, showGlobalSitesLinks } = header;
    return {
        isMobile,
        showGlobalSitesLinks,
    };
};

export default connect(mapStateToProps)(FooterBottomRow);
